import { ChevronDown, ChevronUp } from '@untitled-ui/icons-react/build/cjs';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertMessage from 'src/components/AlertMessage';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import MapBox from 'src/components/MapBox/MapBox';
import Toggle from 'src/components/Toggle/Toggle';

const initFormData = {
    fullAddress: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postal: '',
    latitude: '',
    longitude: '',
    stateCode: '',
    countryCode: '',
    streetNumber: undefined,
};

const initValidForm = {
    fullAddress: true,
    city: true,
    state: true,
    country: true,
    postal: true,
    latitude: true,
    longitude: true,
    stateCode: true,
    countryCode: true,
};

const requiredFields = ['fullAddress', 'city', 'state', 'country', 'postal'];

const Address = ({
    isOpen,
    mode,
    handleClose,
    setAddressData,
    addressData,
    status,
    alertMessageShow = false,
    isViewQuote = false,
    // Matches MapBox's own default — satellite (client, 2026-10-09).
    isDefaultSatelliteView = true,
    isOpenFromQuoting = false,
    isViewAddress = false,
    isValidateAddressCheck = false,
}: any) => {
    const formRef = useRef(null);

    const [isValid, setIsValid] = useState(false);
    const [formData, setFormData] = useState<any>(initFormData);
    const [validForm, setValidForm] = useState(initValidForm);
    const [isFullAddressChanged, setIsFullAddressChanged] = useState(false);
    const [fullAddress, setFullAddress] = useState('');
    const [isDetailsVisible, setIsDetailsVisible] = useState(true);
    const [isSaveDisable, setIsSaveDisable] = useState(true);

    const { t } = useTranslation();

    useEffect(() => {
        if (isOpen) {
            if (mode === 'addressUpdate' || mode === 'consigneeAddress' || mode === 'shipperAddress') {
                setFullAddress(addressData?.fullAddress);
                setFormData(addressData);
                setIsFullAddressChanged(true);
            }

            if (mode === 'addressCreate') {
                setFullAddress('');
                setFormData(initFormData);
                setIsFullAddressChanged(false);
            }
            setIsSaveDisable(true);
        } else {
            setFormData(initFormData);
            setFullAddress('');
            setValidForm(initValidForm);
            setIsFullAddressChanged(false);
            setIsSaveDisable(false);
        }
    }, [isOpen]);

    const isFormValid = () => {
        let valid = true;
        const validFormData = {
            fullAddress: true,
            city: true,
            state: true,
            country: true,
            postal: true,
        };

        if (!formData.fullAddress) {
            validFormData.fullAddress = false;
            valid = false;
        }

        if (!formData.city) {
            validFormData.city = false;
            valid = false;
        }

        if (!formData.state) {
            validFormData.state = false;
            valid = false;
        }

        if (!formData.country) {
            validFormData.country = false;
            valid = false;
        }

        if (!formData.postal || formData.postal?.length < 4) {
            validFormData.postal = false;
            valid = false;
        }

        if (!formData?.streetNumber && alertMessageShow) {
            validFormData.fullAddress = false;
            valid = false;
        }
        setValidForm({ ...validForm, ...validFormData });

        return valid;
    };

    useEffect(() => {
        let isFieldsValid = requiredFields.every((requiredField) => !!formData[requiredField]);

        if (formData?.postal?.length < 4) {
            isFieldsValid = false;
        }

        if (formData?.fullAddress) {
            isFormValid();
        }
        setIsValid(isFieldsValid);
    }, [formData]);

    const isFormDataChanged = () => {
        setIsSaveDisable(false);
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        const updatedFormData = { ...formData, [name]: value };

        if ((name === 'streetNumber' || name === 'address1') && updatedFormData.city) {
            const streetPart = [updatedFormData.streetNumber, updatedFormData.address1].filter(Boolean).join(' ');
            const statePart = [updatedFormData.state, updatedFormData.postal].filter(Boolean).join(' ');
            const newFullAddress = [streetPart, updatedFormData.city, statePart, updatedFormData.country].filter(Boolean).join(', ');
            updatedFormData.fullAddress = newFullAddress;
            setFullAddress(newFullAddress);
        }

        setFormData(updatedFormData);
        isFormDataChanged();
    };

    const handleAutocompleteChange = (value: any) => {
        if (value.type === 'blur') {
            if (!formData.fullAddress) {
                setFormData(initFormData);
                setValidForm(initValidForm);
                setIsFullAddressChanged(false);
            }

            return;
        }

        if (alertMessageShow) {
            setValidForm(initValidForm);
        }

        setFormData(value);
        setFullAddress(value.fullAddress || '');
        setIsFullAddressChanged(true);

        // Only enable save if the selected address differs from the original
        const isSameAddress =
            addressData &&
            value.fullAddress === addressData.fullAddress &&
            value.city === addressData.city &&
            value.state === addressData.state &&
            value.country === addressData.country &&
            value.postal === addressData.postal;

        if (!isSameAddress) {
            isFormDataChanged();
        }
    };

    const handleSubmit = async (event: any) => {
        event?.preventDefault();

        if (!isFormValid()) {
            return true;
        }
        setAddressData(formData);
        handleClose();
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event?.preventDefault();
        }
    };

    const closeAddress = () => {
        setIsDetailsVisible(true);
        handleClose();
    };

    const MapComponent = useMemo(
        () => (
            <div className={`[&>div]:!bg-transparent ${isViewQuote || isViewAddress ? 'pointer-events-none' : '!pointer-events-auto'}`}>
                <MapBox
                    address={formData}
                    id={'addressMap'}
                    height={261}
                    onReverseAddressFunc={handleAutocompleteChange}
                    className="rounded-[20px]"
                    isDefaultSatelliteView={isDefaultSatelliteView}
                    isOpenFromQuoting={isOpenFromQuoting}
                />
            </div>
        ),
        [isFullAddressChanged, formData.fullAddress]
    );

    const toggleDetails = () => {
        setIsDetailsVisible((prev) => !prev);
    };

    return (
        <>
            {isOpen && (
                <CommonModal
                    title={t('Common.Address_Selection')}
                    handleClose={closeAddress}
                    size={'max-w-[688px]'}
                    primaryBtnText={isViewQuote || isViewAddress ? '' : t('Common.Save_Changes')}
                    secondaryBtnText={t('Common.Cancel')}
                    secondaryBtnOnClick={closeAddress}
                    primaryBtnOnClick={handleSubmit}
                    primaryBtnDisabled={!isValid || (alertMessageShow && !formData?.streetNumber) || isSaveDisable}
                >
                    <form ref={formRef} className="p-4 space-y-5" onSubmit={handleSubmit} onKeyDown={handleKeyPress}>
                        <>
                            {alertMessageShow && formData?.streetNumber !== undefined && !formData?.streetNumber && (
                                <AlertMessage className="mb-2" style="error" label={t('QuotingHub.QuotingDashboard.Address_must_be_complete')} />
                            )}

                            {status && (
                                <AutoCompleteGoogle
                                    inputName="fullAddress"
                                    labelRight={
                                        <div className="flex items-center gap-x-2.5 cursor-pointer text-primary" onClick={toggleDetails}>
                                            <h6 className="text-xs font-medium">{t('Common.View_Details')}</h6>
                                            {isDetailsVisible ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                        </div>
                                    }
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_address')}
                                    className={`form_control truncate ${isViewQuote || isViewAddress ? 'pointer-events-none' : '!pointer-events-auto'}`}
                                    label={t('Common.Address')}
                                    labelClassName="mb-0 block"
                                    labelWrapClassName="mb-1.5 !items-center"
                                    onAddressInputChange={setFullAddress}
                                    onChangeFunc={handleAutocompleteChange}
                                    onBlur={handleAutocompleteChange}
                                    errorText={!validForm.fullAddress ? t('ValidationMessages.Address_is_required') : null}
                                    value={fullAddress}
                                    inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-map-input' : ''}`}
                                    disabled={isViewQuote || isViewAddress}
                                    isValidateAddressCheck={isValidateAddressCheck}
                                />
                            )}
                            <div>{MapComponent}</div>
                            {isDetailsVisible && (
                                <div className="w-full space-y-3">
                                    <div className="flex gap-3">
                                        <div className=" w-1/2 ">
                                            <InputText
                                                inputName="streetNumber"
                                                placeholder={t('QuotingHub.QuotingDashboard.Enter_street_number')}
                                                className="form_control"
                                                label={t('QuotingHub.QuotingDashboard.Street_No')}
                                                labelClassName="block mb-1.5"
                                                value={formData.streetNumber || ''}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress}
                                            />
                                        </div>
                                        <div className=" w-1/2 ">
                                            <InputText
                                                inputName="address1"
                                                placeholder={t('QuotingHub.QuotingDashboard.Enter_address_1')}
                                                className="form_control"
                                                label={t('Common.Street')}
                                                labelClassName="block mb-1.5"
                                                value={formData.address1}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className=" w-1/2 ">
                                            <InputText
                                                inputName="city"
                                                placeholder={t('QuotingHub.Common.Enter_city')}
                                                className="form_control"
                                                label={t('Common.City')}
                                                labelClassName="block mb-1.5"
                                                value={formData.city}
                                                errorText={formData.fullAddress && !validForm.city ? t('AddressBook.validation.City_Required') : null}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress || true}
                                            />
                                        </div>
                                        <div className="w-1/2">
                                            <InputText
                                                inputName="country"
                                                placeholder={t('Common.Enter_country')}
                                                className="form_control"
                                                label={t('Common.Country')}
                                                labelClassName="block mb-1.5"
                                                value={formData.country}
                                                errorText={formData.fullAddress && !validForm.country ? t('AddressBook.validation.Country_Required') : null}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress || true}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="w-1/2">
                                            <InputText
                                                inputName="state"
                                                placeholder={t('Common.Enter_state')}
                                                className="form_control"
                                                label={t('Common.State')}
                                                labelClassName="block mb-1.5"
                                                value={formData.state}
                                                errorText={formData.fullAddress && !validForm.state ? t('AddressBook.validation.State_Required') : null}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress || true}
                                            />
                                        </div>
                                        <div className="w-1/2 ">
                                            <InputText
                                                inputName="postal"
                                                placeholder={t('Common.Enter_postal_code')}
                                                className="form_control"
                                                label={t('Common.Postal_Code')}
                                                labelClassName="block mb-1.5"
                                                value={formData.postal}
                                                errorText={formData.fullAddress && !validForm.postal ? t('AddressBook.validation.Postal_Code_Required') : null}
                                                onChangeFunc={handleInputChange}
                                                inputWrapClass={`${isViewQuote || isViewAddress ? 'transparent-disabled-input' : ''}`}
                                                isShowDisableClass={isViewQuote ? false : true}
                                                disabled={isViewQuote || isViewAddress || true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                        <div className="py-3 px-4 border border-utilityGray200 bg-white rounded-xl hidden">
                            <Toggle
                                label={t('QuotingHub.QuotingDashboard.Push_update_to_Customer_Portal')}
                                labelPosition="left"
                                isChecked={true}
                                onChange={() => {}}
                                labelClassName="justify-between"
                                labelTextClassName="!text-grayLight600 text-xs font-semibold leading-[18px]"
                            />
                        </div>
                    </form>
                </CommonModal>
            )}
        </>
    );
};
export default memo(Address);
